import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { fetchVouchers } from '../adminApi'
import { asVoucherStatus } from './voucherLabels'
import type { VoucherQuery } from '../../core/types/vouchers'
import type { RequestOptions } from '../../core/types/backend'

const SEARCH_DELAY_MS = 300
const FILTER_KEYS = ['q', 'status', 'pagina'] as const

/**
 * De lijst met cadeaubonnen, zoals Bestellingen: filters in het adres (?q=&status=&pagina=), zodat
 * terug uit een bon dezelfde lijst geeft. De server pagineert (25 per pagina).
 */
export function useVouchers() {
  const [params, setParams] = useSearchParams()

  const query = useMemo<VoucherQuery>(() => {
    const page = Number(params.get('pagina'))
    return {
      q: params.get('q')?.trim() || undefined,
      status: asVoucherStatus(params.get('status')),
      page: Number.isInteger(page) && page > 1 ? page : 1,
    }
  }, [params])

  const key = JSON.stringify(query)
  const load = useMemo(() => {
    const q = JSON.parse(key) as VoucherQuery
    return (opts: RequestOptions) => fetchVouchers(q, opts)
  }, [key])
  const { data, status, reload } = useLatestLoad(load)

  const setFilter = useCallback(
    (name: (typeof FILTER_KEYS)[number], value: string | undefined) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set(name, value)
          else next.delete(name)
          if (name !== 'pagina') next.delete('pagina')
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  const clearFilters = useCallback(() => setParams({}, { replace: true }), [setParams])
  const hasFilters = FILTER_KEYS.some((k) => k !== 'pagina' && params.get(k))

  const [search, setSearch] = useState(query.q ?? '')
  useEffect(() => setSearch(query.q ?? ''), [query.q])
  useEffect(() => {
    if (search.trim() === (query.q ?? '')) return
    const t = window.setTimeout(() => setFilter('q', search.trim() || undefined), SEARCH_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [search, query.q, setFilter])

  return { query, page: data, status, reload, setFilter, clearFilters, hasFilters, search, setSearch }
}
